import React, { useState, useRef } from "react";
import Slider from "react-slick";
import { Box, Card, IconButton } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BannerImage } from "../../types/dashboard";

type BannerSliderProps = {
  addButton: boolean;
  bannerImages: BannerImage[];
  bannerImgLogo: string;
  sliderDuration: number;
  handleBannerImageModalOpen: () => void;
};

const BannerSlider: React.FC<BannerSliderProps> = ({
  addButton,
  bannerImages,
  bannerImgLogo,
  sliderDuration,
  handleBannerImageModalOpen,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef<Slider | null>(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: sliderDuration,
    beforeChange: (current: number, next: number) => setActiveSlide(next),
  };

  const isPrevDisabled = activeSlide === 0;
  const isNextDisabled = activeSlide === bannerImages.length - 1;

  return (
    <Card
      sx={{ backgroundColor: "#5D5D5D", boxShadow: "none", borderRadius: 0 }}
    >
      <Box sx={{ position: "relative", width: "100%", height: "300px" }}>
        <Slider ref={sliderRef} {...settings}>
          {bannerImages.map(({ id, bannerUrl, url }) => (
            <Box
              component="img"
              key={id}
              src={bannerUrl}
              alt={`Banner ${id + 1}`}
              sx={{
                width: "100%",
                height: 300,
                maxWidth: "100%",
                objectFit: "fill",
                objectPosition: "center",
                backgroundColor: "#393939",
                cursor: "pointer",
              }}
              onClick={() => {
                if (url) {
                  window.open(url, "_blank");
                }
              }}
            />
          ))}
        </Slider>

        {/* Left Arrow */}
        <IconButton
          sx={{
            position: "absolute",
            top: "50%",
            left: 10,
            transform: "translateY(-50%)",
            backgroundColor: isPrevDisabled ? "#000000" : "#DA291C",
            color: "white",
            width: "26px",
            height: "26px",
            borderRadius: 0,
            "&:hover": {
              backgroundColor: isPrevDisabled ? "#000000" : "#B2221A",
            },
            opacity: isPrevDisabled ? 0.5 : 1,
          }}
          disabled={isPrevDisabled}
          onClick={() => sliderRef.current?.slickPrev()}
        >
          <ArrowBackIos fontSize="small" sx={{ marginLeft: "7px" }} />
        </IconButton>

        {/* Right Arrow */}
        <IconButton
          sx={{
            position: "absolute",
            top: "50%",
            right: 10,
            transform: "translateY(-50%)",
            backgroundColor: isNextDisabled ? "#000000" : "#DA291C",
            color: "white",
            width: "26px",
            height: "26px",
            borderRadius: 0,
            "&:hover": {
              backgroundColor: isNextDisabled ? "#000000" : "#B2221A",
            },
            opacity: isNextDisabled ? 0.5 : 1,
          }}
          disabled={isNextDisabled}
          onClick={() => sliderRef.current?.slickNext()}
        >
          <ArrowForwardIos fontSize="small" sx={{ marginRight: "-1px" }} />
        </IconButton>

        {/* Top Right Logo Button */}
        {addButton && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "grid",
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              sx={{ backgroundColor: "white", borderRadius: 0, padding: 0 }}
              onClick={handleBannerImageModalOpen}
            >
              <Box
                component="img"
                src={bannerImgLogo}
                alt="Banner Logo"
                sx={{ width: 24, height: 24 }}
              />
            </IconButton>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default BannerSlider;
